import { useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import {
  buildMozzoPermissionsSnapshot,
  canManageMozzoInternalUsers,
  canPerformMozzoAction,
  getMozzoAccessRoleLabel,
  getMozzoInternalUserRoleLabel,
  listMozzoAssignableInternalUserRoles,
} from "../../services/local/accessControl";

export const useAccessControl = () => {
  const auth = useAuth();
  const {
    accessMode,
    branchSessionMode,
    currentAccessRole,
    currentBranch,
    currentCompany,
    hasBranchPermission,
    hasWaiterPermission,
    isAdminGeneral,
    isCreatorMode,
    isKioskBranch,
    isKitchenWorkspace,
    isPlatformBackofficeAdmin,
    isWaiterWorkspace,
  } = auth;

  const permissionSnapshot = useMemo(
    () =>
      buildMozzoPermissionsSnapshot({
        accessMode,
        branchSessionMode,
        currentBranch,
        currentCompany,
        hasBranchPermission,
        isAdminGeneral,
        isCreatorMode,
        isKioskBranch,
        isPlatformBackofficeAdmin,
      }),
    [
      accessMode,
      branchSessionMode,
      currentBranch,
      currentCompany,
      hasBranchPermission,
      isAdminGeneral,
      isCreatorMode,
      isKioskBranch,
      isPlatformBackofficeAdmin,
    ]
  );

  const canManageInternalUsers = useMemo(
    () => canManageMozzoInternalUsers(currentAccessRole),
    [currentAccessRole]
  );

  const canPerformAction = useCallback(
    (actionKey) =>
      canPerformMozzoAction(actionKey, {
        accessRole: currentAccessRole,
        hasBranchPermission,
        hasWaiterPermission,
        isPlatformBackofficeAdmin,
      }),
    [
      currentAccessRole,
      hasBranchPermission,
      hasWaiterPermission,
      isPlatformBackofficeAdmin,
    ]
  );

  return {
    ...auth,
    currentAccessRole,
    currentAccessRoleLabel: getMozzoAccessRoleLabel(currentAccessRole),
    canManageInternalUsers,
    canPerformAction,
    permissionSnapshot,
    assignableInternalUserRoles: listMozzoAssignableInternalUserRoles(),
    getInternalUserRoleLabel: getMozzoInternalUserRoleLabel,
    isKitchenWorkspace,
    isWaiterWorkspace,
  };
};

export default useAccessControl;
